/**
 * RKC · Plantilla de factura térmica PROFESIONAL (independiente de la UI).
 *
 * Objetivos técnicos (por qué este archivo existe):
 * - El ticket es TEXTO REAL + líneas vectoriales (bordes CSS). Nunca una imagen,
 *   nunca un screenshot. Así la impresora recibe vectores nítidos.
 * - El ancho del documento coincide con el ANCHO ÚTIL real del papel:
 *     80 mm -> 72 mm imprimibles (576 puntos @ 203 dpi)
 *     58 mm -> 48 mm imprimibles (384 puntos @ 203 dpi)
 *   El contenido se alinea a la izquierda (el cabezal de impresión arranca en x=0),
 *   por lo que no se pierde papel ni se corta contenido por la derecha.
 * - La ALTURA es dinámica: no se fija ninguna altura de página. El shell de Electron
 *   mide el contenido renderizado y calcula el `pageSize` exacto (ver main.js).
 * - No se usa `transform: scale()` ni `zoom` para "encajar" nada.
 * - Los campos de facturación electrónica (e-NCF, QR, código de seguridad, etc.)
 *   son CONDICIONALES: solo aparecen cuando la facturación fiscal está activa.
 */

export type ThermalPaper = "pos58" | "pos80";

/** Cómo debe mostrarse el bloque de forma de pago. */
export interface ThermalItem {
  description: string;
  quantity: number;
  unitPrice: number;
  discount?: number | null;
  lineTotal: number;
}

export interface ThermalCompany {
  name: string;
  rnc?: string | null;
  address?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  logoUrl?: string | null;
  headerText?: string | null;
}

export interface ThermalInvoiceMeta {
  number?: string | null;
  /** Título grande del documento, p. ej. "FACTURA". */
  documentTitle?: string | null;
  dateLabel: string;
  timeLabel: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  /** Suma de cantidades, para el renglón "Items". */
  unitCount: number;
}

export interface ThermalCustomer {
  name?: string | null;
  document?: string | null;
  phone?: string | null;
  address?: string | null;
  paymentLabel?: string | null;
}

export interface ThermalVisibility {
  showLogo?: boolean;
  showRnc?: boolean;
  showAddress?: boolean;
  showPhone?: boolean;
  showSocial?: boolean;
  showCustomer?: boolean;
  showCashier?: boolean;
  showDiscount?: boolean;
  showTax?: boolean;
  showNcf?: boolean;
  showThanks?: boolean;
  showReturnMessage?: boolean;
  thanksText?: string | null;
  returnText?: string | null;
  footerText?: string | null;
}

/** Datos de facturación electrónica. SOLO se renderizan si `enabled` es true. */
export interface ThermalFiscal {
  enabled?: boolean;
  title?: string | null;
  ncfLabel?: string | null;
  ncf?: string | null;
  ncfExpiry?: string | null;
  securityCode?: string | null;
  /** QR como data URL de ALTA resolución (generado, nunca un screenshot). */
  qrDataUrl?: string | null;
  signedAt?: string | null;
}

export interface ThermalTicketData {
  company: ThermalCompany;
  invoice: ThermalInvoiceMeta;
  items: ThermalItem[];
  customer?: ThermalCustomer | null;
  cashier?: string | null;
  visibility?: ThermalVisibility | null;
  fiscal?: ThermalFiscal | null;
}

interface ThermalMetrics {
  pageWidthMm: number;
  contentWidthMm: number;
  padTopMm: number;
  padBottomMm: number;
  fs: number;
  fsSmall: number;
  fsCompany: number;
  fsSection: number;
  fsDoc: number;
  fsTotal: number;
  logoMaxWMm: number;
  logoMaxHMm: number;
  qrMm: number;
}

const METRICS: Record<ThermalPaper, ThermalMetrics> = {
  pos80: {
    pageWidthMm: 80,
    contentWidthMm: 72,
    padTopMm: 3,
    padBottomMm: 16,
    fs: 13,
    fsSmall: 11.5,
    fsCompany: 19,
    fsSection: 13.5,
    fsDoc: 20,
    fsTotal: 18,
    logoMaxWMm: 34,
    logoMaxHMm: 18,
    qrMm: 26,
  },
  pos58: {
    pageWidthMm: 58,
    contentWidthMm: 48,
    padTopMm: 2.5,
    padBottomMm: 12,
    fs: 10.6,
    fsSmall: 9.4,
    fsCompany: 15,
    fsSection: 11.5,
    fsDoc: 15.5,
    fsTotal: 14,
    logoMaxWMm: 22,
    logoMaxHMm: 13,
    qrMm: 20,
  },
};

function escapeHtml(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/** Número con separador de miles y 2 decimales (sin símbolo, aprovecha el ancho). */
function num(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString("es-DO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function qty(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString("es-DO", { maximumFractionDigits: 3 });
}

function pad3(value: number): string {
  return String(Math.max(0, Math.round(value))).padStart(3, "0");
}

function buildStyles(m: ThermalMetrics): string {
  return `
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; background: #fff; }
    @page { size: ${m.pageWidthMm}mm auto; margin: 0; }
    body {
      width: ${m.pageWidthMm}mm;
      font-family: Arial, Helvetica, "Segoe UI", Roboto, sans-serif;
      color: #000;
      font-size: ${m.fs}px;
      line-height: 1.5;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }
    /* El ancho útil real del papel. Sin overflow:hidden para no recortar en silencio. */
    .sheet {
      width: ${m.contentWidthMm}mm;
      max-width: 100%;
      margin: 0;
      padding: ${m.padTopMm}mm 0 ${m.padBottomMm}mm;
      overflow-x: visible;
    }
    .sheet > * { max-width: 100%; }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: 800; }
    .uppercase { text-transform: uppercase; }
    .logo {
      display: block; margin: 0 auto 2mm;
      max-width: ${m.logoMaxWMm}mm; max-height: ${m.logoMaxHMm}mm;
      width: auto; height: auto; object-fit: contain;
    }
    .company { font-size: ${m.fsCompany}px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
    .sub { font-size: ${m.fsSmall}px; }
    .kv { font-size: ${(m.fs - 0.5).toFixed(1)}px; }
    .rule { border: 0; border-top: 2px dashed #000; height: 0; margin: 2.4mm 0; }
    .sec { font-size: ${m.fsSection}px; font-weight: 800; text-transform: uppercase; letter-spacing: .8px; text-align: center; }
    .doctitle { font-size: ${m.fsDoc}px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; }
    .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 2mm; }
    /* La etiqueta (izquierda) puede encoger y partirse; el valor (derecha) nunca se corta. */
    .row > span:first-child { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
    .row .val { flex: 0 0 auto; text-align: right; white-space: nowrap; font-weight: 700; }
    .row .val.wrap { white-space: normal; word-break: break-word; }
    .items .ihead {
      display: flex; justify-content: space-between; gap: 2mm;
      font-size: ${(m.fs - 0.5).toFixed(1)}px; font-weight: 800; text-transform: uppercase; letter-spacing: .5px;
      border-bottom: 2px solid #000; padding-bottom: 1.2mm; margin-bottom: 1.6mm;
    }
    .items .ihead > span:first-child { flex: 1 1 auto; min-width: 0; }
    .items .ihead > span:last-child { flex: 0 0 auto; white-space: nowrap; }
    .item { margin-bottom: 2.4mm; }
    .item .desc { font-size: ${m.fs}px; font-weight: 700; word-break: break-word; overflow-wrap: anywhere; }
    .item .calc { display: flex; justify-content: space-between; gap: 2mm; font-size: ${(m.fs - 0.5).toFixed(1)}px; }
    .item .calc > span:first-child { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
    .item .calc .amt { flex: 0 0 auto; white-space: nowrap; font-weight: 800; }
    .totals { margin-top: 2mm; }
    .totals .row { font-size: ${(m.fs - 0.5).toFixed(1)}px; margin-bottom: .8mm; }
    .grand {
      font-size: ${m.fsTotal}px; font-weight: 800; letter-spacing: .3px;
      border-top: 2.5px solid #000; margin-top: 2mm; padding-top: 1.6mm;
    }
    .note { font-size: ${(m.fs - 1.5).toFixed(1)}px; }
    .footer { font-size: ${(m.fsSmall + 0.5).toFixed(1)}px; }
    .qr { display: block; margin: 2mm auto 1mm; width: ${m.qrMm}mm; height: ${m.qrMm}mm; image-rendering: pixelated; }
    @media print {
      html, body { width: ${m.pageWidthMm}mm; }
      .item, .totals, .grand, .qr, img { break-inside: avoid; page-break-inside: avoid; }
    }
  `;
}

function buildHeader(data: ThermalTicketData, m: ThermalMetrics): string {
  const { company } = data;
  const v = data.visibility ?? {};
  const logo =
    v.showLogo !== false && company.logoUrl
      ? `<img class="logo" src="${escapeHtml(company.logoUrl)}" alt="Logo" />`
      : "";
  const lines: string[] = [];
  if (v.showRnc !== false && company.rnc) lines.push(`RNC: ${escapeHtml(company.rnc)}`);
  if (v.showAddress !== false && company.address) lines.push(escapeHtml(company.address));
  if (v.showPhone !== false && company.phone) lines.push(`Tel: ${escapeHtml(company.phone)}`);
  if (v.showSocial !== false && company.whatsapp) lines.push(`WhatsApp: ${escapeHtml(company.whatsapp)}`);
  if (company.email) lines.push(escapeHtml(company.email));

  return `
    <div class="center">
      ${logo}
      <div class="company">${escapeHtml(company.name)}</div>
      ${company.headerText ? `<div class="sub">${escapeHtml(company.headerText)}</div>` : ""}
      ${lines.map((line) => `<div class="kv">${line}</div>`).join("")}
    </div>`;
}

function buildFiscalBlock(fiscal: ThermalFiscal | null | undefined, v: ThermalVisibility): string {
  // Sin comprobante fiscal (o con el NCF apagado) no se imprime nada fiscal.
  if (!fiscal?.enabled || v.showNcf === false) return "";
  const parts: string[] = [];
  parts.push(`<div class="sec">${escapeHtml(fiscal.title || "Factura de Crédito Fiscal Electrónica")}</div>`);
  if (v.showNcf !== false && fiscal.ncf) {
    parts.push(`<div class="kv">${escapeHtml(fiscal.ncfLabel || "NCF")}: ${escapeHtml(fiscal.ncf)}</div>`);
  }
  if (fiscal.ncfExpiry) parts.push(`<div class="kv">NCF VENCE: ${escapeHtml(fiscal.ncfExpiry)}</div>`);
  if (fiscal.securityCode) {
    parts.push(`<div class="center kv">Código de Seguridad: ${escapeHtml(fiscal.securityCode)}</div>`);
  }
  if (fiscal.qrDataUrl) parts.push(`<img class="qr" src="${escapeHtml(fiscal.qrDataUrl)}" alt="QR" />`);
  if (fiscal.signedAt) parts.push(`<div class="center note">Fecha de Firma Digital: ${escapeHtml(fiscal.signedAt)}</div>`);
  return parts.join("");
}

function buildItems(data: ThermalTicketData): string {
  if (data.items.length === 0) return "";
  const rows = data.items
    .map((item) => {
      const hasDiscount = Number(item.discount ?? 0) > 0;
      const discount = hasDiscount ? `  ·  Desc: -${num(item.discount)}` : "";
      return `
        <div class="item">
          <div class="desc">${escapeHtml(item.description)}</div>
          <div class="calc">
            <span>${qty(item.quantity)} x ${num(item.unitPrice)}${discount}</span>
            <span class="amt">${num(item.lineTotal)}</span>
          </div>
        </div>`;
    })
    .join("");
  return `
    <div class="items">
      <div class="ihead"><span>Descripción</span><span>Importe</span></div>
      ${rows}
    </div>`;
}

function buildCustomer(data: ThermalTicketData): string {
  const v = data.visibility ?? {};
  if (v.showCustomer === false) return "";
  const c = data.customer;
  const rows: string[] = [];
  if (c?.name) rows.push(`<div class="row kv"><span>Cliente</span><span class="val wrap">${escapeHtml(c.name)}</span></div>`);
  if (c?.document) rows.push(`<div class="row kv"><span>RNC/Cédula</span><span class="val">${escapeHtml(c.document)}</span></div>`);
  if (c?.phone) rows.push(`<div class="row kv"><span>Teléfono</span><span class="val">${escapeHtml(c.phone)}</span></div>`);
  if (c?.address) rows.push(`<div class="row kv"><span>Dirección</span><span class="val wrap">${escapeHtml(c.address)}</span></div>`);
  if (c?.paymentLabel) rows.push(`<div class="row kv"><span>Forma de pago</span><span class="val">${escapeHtml(c.paymentLabel)}</span></div>`);
  if (rows.length === 0) return "";
  return `<hr class="rule" /><div class="sec">Datos del Cliente</div>${rows.join("")}`;
}

function buildTotals(data: ThermalTicketData): string {
  const v = data.visibility ?? {};
  const { invoice } = data;
  const rows: string[] = [];
  rows.push(`<div class="row"><span>Subtotal</span><span class="val">${num(invoice.subtotal)}</span></div>`);
  if (v.showDiscount !== false && Number(invoice.discountTotal) > 0) {
    rows.push(`<div class="row"><span>Descuento</span><span class="val">-${num(invoice.discountTotal)}</span></div>`);
  }
  if (v.showTax !== false && Number(invoice.taxTotal) > 0) {
    rows.push(`<div class="row"><span>ITBIS</span><span class="val">${num(invoice.taxTotal)}</span></div>`);
  }
  rows.push(`<div class="row grand"><span>TOTAL A PAGAR</span><span class="val">${num(invoice.total)}</span></div>`);
  return `<hr class="rule" /><div class="totals">${rows.join("")}</div>`;
}

/**
 * Genera el documento HTML completo de un ticket térmico profesional.
 * Devuelve un documento autónomo listo para imprimir (Electron o navegador).
 */
export function buildThermalTicketHtml(data: ThermalTicketData, paper: ThermalPaper): string {
  const m = METRICS[paper];
  const v = data.visibility ?? {};
  const { invoice } = data;

  const metaRows: string[] = [];
  if (invoice.number) {
    metaRows.push(`<div class="row kv"><span>Factura No.</span><span class="val">${escapeHtml(invoice.number)}</span></div>`);
  }
  metaRows.push(`<div class="row kv"><span>Fecha</span><span class="val">${escapeHtml(invoice.dateLabel)}</span></div>`);
  metaRows.push(`<div class="row kv"><span>Hora</span><span class="val">${escapeHtml(invoice.timeLabel)}</span></div>`);

  const cashierRow =
    v.showCashier !== false && data.cashier
      ? `<div class="row kv"><span>Le atendió</span><span class="val">${escapeHtml(data.cashier)}</span></div>`
      : "";

  const footerParts: string[] = [];
  if (v.showThanks !== false) {
    footerParts.push(`<div class="center footer bold">${escapeHtml(v.thanksText || "¡Gracias por su compra!")}</div>`);
  }
  if (v.showReturnMessage !== false && v.returnText) {
    footerParts.push(`<div class="center note">${escapeHtml(v.returnText)}</div>`);
  }
  if (v.footerText) footerParts.push(`<div class="center note">${escapeHtml(v.footerText)}</div>`);

  return `<!doctype html><html lang="es"><head><meta charset="utf-8" />
<title>Factura ${escapeHtml(invoice.number ?? "")}</title>
<style>${buildStyles(m)}</style></head>
<body>
  <div class="sheet">
    ${buildHeader(data, m)}
    <hr class="rule" />
    <div class="center"><div class="doctitle">${escapeHtml(invoice.documentTitle || "Factura")}</div></div>
    ${buildFiscalBlock(data.fiscal, v)}
    <hr class="rule" />
    ${metaRows.join("")}
    ${buildCustomer(data)}
    <hr class="rule" />
    ${buildItems(data)}
    ${buildTotals(data)}
    <hr class="rule" />
    <div class="row note">
      <span>Renglones: ${pad3(data.items.length)}</span>
      <span>Items: ${pad3(invoice.unitCount)}</span>
    </div>
    ${cashierRow}
    <hr class="rule" />
    ${footerParts.join("")}
  </div>
</body></html>`;
}